import React from "react";
import type { Placement } from "@floating-ui/react";
import {
  generateClassNameWrapper,
  type ButtonStyle,
} from "../ButtonUtils/ButtonUtils";
import "./IconButton.scss";
import { getDataAnalyticsProps } from "../../../../utils";
import { Tooltip } from "../../../components";
import { useIcon } from "../../../assets/useIcon";

export interface IconButtonProps {
  size?: "xxs" | "xs" | "s" | "small" | "medium" | "large" | "xl";
  // Doubles as a falsy toggle at runtime: SVG-only buttons pass isIcon={false}.
  isIcon?: string | false;
  svgIcon?: string;
  isGhost?: boolean;
  isRound?: boolean;
  hasBg?: string | boolean;
  isHighSeverityAlert?: boolean;
  startHidden?: boolean;
  buttonStyle?: ButtonStyle | "" | false;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: string;
  name?: string;
  className?: string;
  wrapperClassName?: string;
  // null is accepted as a runtime "no tooltip" value (read via `withTooltip?.`).
  withTooltip?:
    | {
        // Passed through to Tooltip, which renders any ReactNode.
        content?: React.ReactNode;
        place?: Placement;
        className?: string;
      }
    | false
    | null;
  "data-testid"?: string;
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
  // Remaining shared button props are spread through to the underlying button.
  [key: string]: any;
}

const IconButton: React.FC<IconButtonProps> = ({
  size,
  isIcon = "",
  isHighSeverityAlert = false,
  svgIcon = "",
  isGhost = false,
  isRound = false,
  hasBg = false,
  startHidden = false,
  buttonStyle = "",
  onClick = () => {},
  ...shared
}) => {
  const { getSvg } = useIcon();
  // Normalize the tooltip prop: callers may pass `false` (from a `cond && {...}`
  // expression). `false`/`undefined`/`{}` all collapse to no tooltip, matching
  // the prior runtime behavior where reads were optional-chained.
  const tooltipProps = shared?.withTooltip || {};
  const specificProps = {
    [`is-${size}`]: size,
    "is-icon": isIcon,
    "is-svg-icon": svgIcon,
    "is-ghost": isGhost,
    "is-round": isRound,
    "has-bg": hasBg,
  };

  const classNameWrapper = generateClassNameWrapper(
    { ...shared, buttonStyle },
    specificProps
  );

  return (
    <>
      <Tooltip
        type={"info"}
        placement={tooltipProps?.place}
        content={tooltipProps?.content}
        className={tooltipProps?.className}
      >
        <div className={`form-icon-button-wrap ${classNameWrapper}`}>
          <button
            data-testid={`${shared?.["data-testid"]}-btn`}
            className={`form-icon-button ${shared?.className}`}
            type={
              shared?.type as React.ButtonHTMLAttributes<HTMLButtonElement>["type"]
            }
            disabled={shared?.disabled}
            onClick={onClick}
            name={shared?.name}
            // Tooltip content is typically a string here; cast keeps main's
            // runtime pass-through while withTooltip.content is ReactNode.
            aria-label={tooltipProps?.content as string | undefined}
            aria-expanded={shared?.["aria-expanded"]}
            aria-controls={shared?.["aria-controls"]}
            style={startHidden ? { opacity: 0 } : undefined}
            {...getDataAnalyticsProps(shared)}
          >
            {isIcon && (
              <i
                id={`btn-is-icon btn-seem-icon`}
                className={`seem-icon ${isIcon}`}
              />
            )}
            {svgIcon && <img src={getSvg(svgIcon)} alt={svgIcon} />}
          </button>
          {isHighSeverityAlert && <div className={"high-sev-alert"} />}
        </div>
      </Tooltip>
    </>
  );
};

export default IconButton;
